import {NgModule} from '@angular/core';
import {CommonModule} from '@angular/common';
import {HttpClientModule} from '@angular/common/http';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {MomentModule} from 'ngx-moment';
import 'moment/locale/es';
import 'moment/locale/en-gb';
import 'moment/locale/fr';

import {DialogModule} from 'primeng/dialog';
import {TooltipModule} from 'primeng/tooltip';
import {DropdownModule} from 'primeng/dropdown';
import {MultiSelectModule} from 'primeng/multiselect';
import {TableModule} from 'primeng/table';
import {CalendarModule} from 'primeng/calendar';
import {AccordionModule} from 'primeng/accordion';
import {EditorModule} from 'primeng/editor';
import {PaginatorModule} from 'primeng/paginator';
import {OverlayPanelModule} from 'primeng/overlaypanel';
import {PanelMenuModule} from 'primeng/panelmenu';
import {TieredMenuModule} from 'primeng/tieredmenu';
import {TreeTableModule} from 'primeng/treetable';
import {InputSwitchModule} from 'primeng/inputswitch';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {FileUploadModule} from 'primeng/fileupload';
import {OrganizationChartModule} from 'primeng/organizationchart';

import {FrontBaseAngularCoreRoutingModule} from './front-base-angular-core-routing.module';
import {ValidationDirective} from './directives/validation.directive';
import {ValidationElementComponent} from './components/validation-element/validation-element.component';
import {ValidationErrorTextComponent} from './components/validation-error-text/validation-error-text.component';
import {InputComponent} from './components/input/input.component';
import {LabelComponent} from './components/label/label.component';
import {CheckComponent} from './components/check/check.component';
import {CheckDynamicComponent} from './components/check-dynamic/check-dynamic.component';
import {DatepickerComponent} from './components/datepicker/datepicker.component';
import {MultilineComponent} from './components/multiline/multiline.component';
import {OverlayPanelComponent} from './components/overlay-panel/overlay-panel.component';
import {RadioComponent} from './components/radio/radio.component';
import {RadioDynamicComponent} from './components/radio-dynamic/radio-dynamic.component';
import {RadioButtonComponent} from './components/radio-button/radio-button.component';
import {RichTextComponent} from './components/rich-text/rich-text.component';
import {SelectorComponent} from './components/selector/selector.component';
import {SelectorDialogComponent} from './components/selector-dialog/selector-dialog.component';
import {SelectorMultipleComponent} from './components/selector-multiple/selector-multiple.component';
import {TableComponent} from './components/table/table.component';
import {ListDynamicComponent} from './components/list-dynamic/list-dynamic.component';
import {LoaderComponent} from './components/loader/loader.component';
import {ProvideParentFormDirective} from './directives/provide-parent-form.directive';
import {ClickOutsideCloseDirective} from './directives/click-outside-close.directive';
import {GoToRouteIconComponent} from './components/icons/go-to-route-icon/go-to-route-icon.component';
import {ClickStopPropagationDirective} from './directives/click-stop-propagation.directive';
import {TreeTableComponent} from './components/tree-table/tree-table.component';
import {GoToRouteButtonComponent} from './components/buttons/go-to-route-button/go-to-route-button.component';
import {SwitchComponent} from './components/switch/switch.component';
import {VerticalMenuComponent} from './components/vertical-menu/vertical-menu.component';
import {FileUploadComponent} from './components/file-upload/file-upload.component';
import {StorageDirective} from './directives/storage.directive';
import {HighlightDirective} from './directives/highlight.directive';
import {AutoFocusDirective} from './directives/auto-focus.directive';
import {MenuIconComponent} from './components/icons/menu-icon/menu-icon.component';
import {SvgIconComponent} from './components/icons/svg-icon/svg-icon.component';
import {ConcatNamesPipe} from './pipes/concat-names.pipe';
import {SafeUrlPipe} from './pipes/safe-url.pipe';
import {OrgChartComponent} from './components/org-chart/org-chart.component';
import {PaginatorComponent} from './components/paginator/paginator.component';
import {DocumentationPageComponent} from './documentation/documentation-page/documentation-page.component';
import {DocumentationFieldsComponent} from './documentation/documentation-page/documentation-fields/documentation-fields.component';
import {
  DocumentationAccordionComponent
} from './documentation/documentation-page/documentation-accordion/documentation-accordion.component';
import {DocumentationOrgChartComponent} from './documentation/documentation-page/documentation-org-chart/documentation-org-chart.component';
import {DocumentationTableComponent} from './documentation/documentation-page/documentation-table/documentation-table.component';
import {SelectorCustomComponent} from './components/selector-custom/selector-custom.component';
import {SelectorIconComponent} from './components/selector-icon/selector-icon.component';
import {GoUpButtonComponent} from './components/go-up-button/go-up-button.component';
import {InfiniteScrollComponent} from './components/infinite-scroll/infinite-scroll.component';
import {CaptchaComponent} from './components/captcha/captcha.component';
import {GetAgePipe} from './pipes/get-age.pipe';
import {ExternalReferencePipe} from './pipes/external-reference.pipe';
import {ExternalReferenceDirective} from './directives/external-reference.directive';

@NgModule({
  declarations: [
    ValidationDirective,
    ValidationElementComponent,
    ValidationErrorTextComponent,
    ProvideParentFormDirective,
    InputComponent,
    LabelComponent,
    CheckComponent,
    CheckDynamicComponent,
    DatepickerComponent,
    MultilineComponent,
    OverlayPanelComponent,
    RadioComponent,
    RadioDynamicComponent,
    RadioButtonComponent,
    RichTextComponent,
    SelectorComponent,
    SelectorCustomComponent,
    SelectorIconComponent,
    SelectorDialogComponent,
    SelectorMultipleComponent,
    TableComponent,
    ListDynamicComponent,
    LoaderComponent,
    ClickOutsideCloseDirective,
    GoToRouteIconComponent,
    ClickStopPropagationDirective,
    TreeTableComponent,
    GoToRouteButtonComponent,
    SwitchComponent,
    VerticalMenuComponent,
    FileUploadComponent,
    StorageDirective,
    HighlightDirective,
    AutoFocusDirective,
    MenuIconComponent,
    SvgIconComponent,
    ConcatNamesPipe,
    SafeUrlPipe,
    GetAgePipe,
    OrgChartComponent,
    PaginatorComponent,
    DocumentationPageComponent,
    DocumentationFieldsComponent,
    DocumentationAccordionComponent,
    DocumentationOrgChartComponent,
    DocumentationTableComponent,
    GoUpButtonComponent,
    InfiniteScrollComponent,
    CaptchaComponent,
    ExternalReferencePipe,
    ExternalReferenceDirective
  ],
  imports: [
    CommonModule,
    FrontBaseAngularCoreRoutingModule,
    FormsModule,
    HttpClientModule,
    MomentModule,
    TranslateModule,
    DialogModule,
    TooltipModule,
    DropdownModule,
    MultiSelectModule,
    TableModule,
    CalendarModule,
    AccordionModule,
    EditorModule,
    PaginatorModule,
    OverlayPanelModule,
    TieredMenuModule,
    TreeTableModule,
    InputSwitchModule,
    ProgressSpinnerModule,
    FileUploadModule,
    OrganizationChartModule
  ],
  exports: [
    MomentModule,
    DialogModule,
    TooltipModule,
    DropdownModule,
    MultiSelectModule,
    TableModule,
    CalendarModule,
    AccordionModule,
    EditorModule,
    PaginatorModule,
    OverlayPanelModule,
    TieredMenuModule,
    InputSwitchModule,
    ProgressSpinnerModule,
    FileUploadModule,
    OrganizationChartModule,
    ValidationDirective,
    ValidationElementComponent,
    ValidationErrorTextComponent,
    ProvideParentFormDirective,
    InputComponent,
    LabelComponent,
    CheckComponent,
    CheckDynamicComponent,
    DatepickerComponent,
    MultilineComponent,
    OverlayPanelComponent,
    PanelMenuModule,
    RadioComponent,
    RadioDynamicComponent,
    RadioButtonComponent,
    RichTextComponent,
    SelectorComponent,
    SelectorCustomComponent,
    SelectorIconComponent,
    SelectorDialogComponent,
    SelectorMultipleComponent,
    TableComponent,
    ListDynamicComponent,
    LoaderComponent,
    ClickOutsideCloseDirective,
    GoToRouteIconComponent,
    ClickStopPropagationDirective,
    TreeTableComponent,
    GoToRouteButtonComponent,
    SwitchComponent,
    VerticalMenuComponent,
    FileUploadComponent,
    StorageDirective,
    HighlightDirective,
    AutoFocusDirective,
    MenuIconComponent,
    SvgIconComponent,
    ConcatNamesPipe,
    SafeUrlPipe,
    GetAgePipe,
    OrgChartComponent,
    PaginatorComponent,
    GoUpButtonComponent,
    InfiniteScrollComponent,
    CaptchaComponent,
    ExternalReferencePipe,
    ExternalReferenceDirective
  ]
})
export class FrontBaseAngularCoreModule {
}
